/**
 * CRM Multi-Compagnia · Lista Prodotti
 */
import { useState } from "react";
import { Package, Plus, Edit3, Trash2 } from "lucide-react";
import {
  api, useApi, useDebounced, DataTable, Spinner, ErrorBox, Btn,
  Field, TextInput, Select, RamoChip, RAMI_LABEL, toast
} from "@/components/crm-multi/shared";

const RAMI_LIST = Object.keys(RAMI_LABEL);

export default function CrmMultiProdottiPage() {
  const [q, setQ] = useState("");
  const [ramo, setRamo] = useState("");
  const [companyId, setCompanyId] = useState("");
  useDebounced(q);
  const compagnie = useApi(() => api.get("/companies", { limit: 200 }));
  const list = useApi(() => api.get("/products", { ramo: ramo || undefined, company_id: companyId || undefined, limit: 200 }), [ramo, companyId]);
  const [editing, setEditing] = useState(null);

  const items = (list.data?.items || []).filter(p =>
    !q || p.nome.toLowerCase().includes(q.toLowerCase()) || (p.codice_prodotto || "").toLowerCase().includes(q.toLowerCase())
  );

  const onSave = async (form) => {
    try {
      if (editing.mode === "new") await api.post("/products", form);
      else await api.patch(`/products/${editing.data.id}`, form);
      toast("Prodotto salvato", "success"); setEditing(null); list.refresh();
    } catch (e) { toast(e?.response?.data?.detail?.error_code || e.message, "error"); }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Eliminare il prodotto?")) return;
    try { await api.del(`/products/${id}`); toast("Eliminato", "success"); list.refresh(); }
    catch (e) { toast(e.message, "error"); }
  };

  const compMap = Object.fromEntries((compagnie.data?.items || []).map(c => [c.id, c.nome]));

  return (
    <div className="space-y-4" data-testid="cm-prodotti-page">
      <header className="flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-zinc-900 flex items-center gap-2"><Package className="w-5 h-5" /> Prodotti</h2>
          <p className="text-sm text-zinc-500">{list.data?.total ?? 0} prodotti totali</p>
        </div>
        <Btn variant="primary" onClick={() => setEditing({ mode: "new", data: { nome: "", company_id: "", rami_supportati: [], target_clientela: "entrambi", attivo: true } })} data-testid="cm-prodotti-new">
          <Plus className="w-4 h-4" /> Nuovo prodotto
        </Btn>
      </header>

      <div className="grid md:grid-cols-3 gap-2">
        <TextInput placeholder="Cerca per nome o codice..." value={q} onChange={(e) => setQ(e.target.value)} />
        <Select value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="cm-prodotti-filter-compagnia">
          <option value="">Tutte le compagnie</option>
          {(compagnie.data?.items || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
        </Select>
        <Select value={ramo} onChange={(e) => setRamo(e.target.value)} data-testid="cm-prodotti-filter-ramo">
          <option value="">Tutti i rami</option>
          {RAMI_LIST.map(r => <option key={r} value={r}>{RAMI_LABEL[r]}</option>)}
        </Select>
      </div>

      <ErrorBox error={list.error} />
      {list.loading ? <Spinner /> : (
        <DataTable
          testid="cm-prodotti-table"
          columns={[
            { key: "nome", label: "Prodotto", render: r => <div><p className="font-medium">{r.nome}</p><p className="text-[10px] text-zinc-400 font-mono">{r.codice_prodotto || "—"}</p></div> },
            { key: "compagnia", label: "Compagnia", render: r => <span className="text-xs">{compMap[r.company_id] || "?"}</span> },
            { key: "rami_supportati", label: "Rami", render: r => <div className="flex gap-1 flex-wrap">{r.rami_supportati.map(x => <RamoChip key={x} ramo={x} />)}</div> },
            { key: "target_clientela", label: "Target", render: r => <span className="text-xs capitalize">{r.target_clientela}</span> },
            { key: "azioni", label: "", render: r => (
              <div className="flex gap-1 justify-end">
                <button onClick={(e) => { e.stopPropagation(); setEditing({ mode: "edit", data: r }); }} className="p-1 text-zinc-500 hover:text-emerald-700"><Edit3 className="w-3.5 h-3.5" /></button>
                <button onClick={(e) => { e.stopPropagation(); onDelete(r.id); }} className="p-1 text-zinc-500 hover:text-rose-600"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            )},
          ]}
          rows={items}
          emptyTitle="Nessun prodotto"
          emptyHint="Aggiungi il primo prodotto associandolo a una compagnia."
        />
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="cm-prodotti-edit">
          <div className="absolute inset-0 bg-black/50" onClick={() => setEditing(null)} />
          <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-xl">
            <header className="px-5 py-3 border-b flex justify-between"><h3 className="text-sm font-semibold">{editing.mode === "new" ? "Nuovo prodotto" : "Modifica prodotto"}</h3><button onClick={() => setEditing(null)} className="text-xl">×</button></header>
            <form onSubmit={(e) => { e.preventDefault(); onSave(editing.data); }} className="p-5 space-y-3">
              <Field label="Compagnia" required>
                <Select required value={editing.data.company_id} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, company_id: e.target.value } })}>
                  <option value="">Seleziona...</option>
                  {(compagnie.data?.items || []).map(c => <option key={c.id} value={c.id}>{c.nome}</option>)}
                </Select>
              </Field>
              <Field label="Nome prodotto" required><TextInput required value={editing.data.nome} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, nome: e.target.value } })} /></Field>
              <Field label="Codice prodotto"><TextInput value={editing.data.codice_prodotto || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, codice_prodotto: e.target.value } })} /></Field>
              <Field label="Rami coperti" required>
                <div className="grid grid-cols-3 gap-1 max-h-44 overflow-y-auto border p-2 rounded">
                  {RAMI_LIST.map(r => {
                    const checked = editing.data.rami_supportati?.includes(r);
                    return (
                      <label key={r} className="text-xs flex items-center gap-1 cursor-pointer">
                        <input type="checkbox" checked={!!checked} onChange={() => {
                          const set = new Set(editing.data.rami_supportati || []);
                          checked ? set.delete(r) : set.add(r);
                          setEditing({ ...editing, data: { ...editing.data, rami_supportati: [...set] } });
                        }} />
                        {RAMI_LABEL[r]}
                      </label>
                    );
                  })}
                </div>
              </Field>
              <Field label="Target clientela">
                <Select value={editing.data.target_clientela || "entrambi"} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, target_clientela: e.target.value } })}>
                  <option value="privati">Privati</option><option value="aziende">Aziende</option><option value="entrambi">Entrambi</option>
                </Select>
              </Field>
              <Field label="External SIA Product ID"><TextInput value={editing.data.external_sia_product_id || ""} onChange={(e) => setEditing({ ...editing, data: { ...editing.data, external_sia_product_id: e.target.value } })} /></Field>
              <div className="flex justify-end gap-2 pt-3 border-t"><Btn variant="outline" type="button" onClick={() => setEditing(null)}>Annulla</Btn><Btn variant="primary" type="submit">Salva</Btn></div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
